import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  Lock,
  KeyRound,
  Video,
  MessageSquare,
  UserCheck,
  Server,
  FileCheck,
  EyeOff,
  Wifi,
  ArrowRight,
  Check,
} from 'lucide-react';
import { FREE_MEETING_MAX_DURATION_MINUTES } from '../utils/plan.ts';

const pillars = [
  {
    icon: MessageSquare,
    title: 'Sohbet AES-256-GCM',
    description:
      'Toplantı sohbeti uçtan uca AES-256-GCM ile şifrelenir. Oda anahtarı katılımcılar arasında ECDH ile paylaşılır; sunucu sohbet içeriğini düz metin olarak göremez.',
  },
  {
    icon: Video,
    title: 'Medya koruması',
    description:
      'Destekleyen tarayıcılarda (Chromium tabanlı) ses, görüntü ve ekran paylaşımı Insertable Streams ile AES-256-GCM çerçeve şifrelemesi alır. Tüm WebRTC oturumlarında taşıma katmanında DTLS-SRTP kullanılır.',
  },
  {
    icon: UserCheck,
    title: 'Bekleme odası ve host kontrolü',
    description:
      'Toplantı sahibi kimlerin gireceğine karar verir. Katılımcıları onaylayabilir, sessize alabilir veya toplantıdan çıkarabilir.',
  },
  {
    icon: KeyRound,
    title: 'Güvenli hesap erişimi',
    description:
      'Kimlik doğrulama token ile yönetilir. Şifre sıfırlama e-posta bağlantısı ile yapılır; Google ile giriş seçeneği sunulur.',
  },
];

const practices = [
  {
    icon: Wifi,
    title: 'Şifreli taşıma',
    text: 'HTTPS ve WebRTC DTLS-SRTP ile veri aktarımı şifrelenir.',
  },
  {
    icon: EyeOff,
    title: 'Link ve süre sınırları',
    text: `Anlık toplantı linkleri ve ücretsiz planda canlı oturum süresi (en fazla ${FREE_MEETING_MAX_DURATION_MINUTES} dk) ile açık kalan odalar sınırlanır.`,
  },
  {
    icon: Server,
    title: 'Sinyal sunucusu',
    text: 'Sinyalleşme eşleştirmeyi sağlar; E2E sohbet anahtarları istemci tarafında türetilir ve paylaşılır.',
  },
  {
    icon: FileCheck,
    title: 'KVKK odaklı yaklaşım',
    text: 'Kişisel verilerin işlenmesinde şeffaflık ve minimum gereklilik ilkesi hedeflenir.',
  },
];

const checklist = [
  'Sohbet mesajları AES-256-GCM ile şifrelenir',
  'Medya için DTLS-SRTP (ve desteklenen tarayıcılarda ek AES-256 çerçeve şifrelemesi)',
  'Bekleme odası ile giriş onayı',
  'Host: sessize alma ve katılımcı çıkarma',
  'HTTPS üzerinden güvenli web erişimi',
  'Toplantı linki / canlı oturum süre politikaları',
];

function Security() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-24 pb-20">
      <section className="px-6 pb-14 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl -top-48 -left-48" />
          <div className="absolute w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl -bottom-48 -right-48" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-sm mb-5">
            <Shield className="w-4 h-4" />
            Güvenlik & gizlilik
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            TBKMeet Güvenlik
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Toplantılarınızı korumak için şifreleme, erişim kontrolü ve güvenli altyapıyı
            ürünün içine gömdük — pazarlama cümleleri değil, gerçek özellikler.
          </p>
        </div>
      </section>

      <section className="px-6 pb-14">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-5">
          {pillars.map((item) => (
            <div
              key={item.title}
              className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 hover:bg-white/[0.07] transition"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center mb-4">
                <item.icon className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{item.title}</h2>
              <p className="text-gray-400 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 pb-14">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Nasıl koruyoruz?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {practices.map((item) => (
              <div
                key={item.title}
                className="bg-white/5 rounded-2xl border border-white/10 p-5"
              >
                <item.icon className="w-5 h-5 text-purple-300 mb-3" />
                <h3 className="text-white font-semibold mb-1.5">{item.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-14">
        <div className="max-w-3xl mx-auto bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-5">
            <Lock className="w-6 h-6 text-emerald-400" />
            <h2 className="text-xl font-bold text-white">Güvenlik özeti</h2>
          </div>
          <ul className="space-y-3">
            {checklist.map((line) => (
              <li key={line} className="flex items-start gap-3 text-gray-300">
                <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
            <p className="text-sm text-gray-500 leading-relaxed">
              Not: Medya çerçeve şifrelemesi tarayıcı desteğine bağlıdır. Desteklenmeyen
              ortamlarda medya yine de WebRTC DTLS-SRTP ile taşınır; sohbet şifrelemesi
              bağımsız çalışmaya devam eder. Host tutanak açtığında konuşma dökümü
              self-hosted Whisper ile TBKMeet sunucusunda yapılır; OpenAI’ye ses gönderilmez.
              Tutanaklar host cihazında saklanır.
            </p>
        </div>
      </section>

      <section className="px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Sorunuz mu var?</h2>
          <p className="text-gray-400 mb-6">
            Güvenlik veya KVKK ile ilgili talepleriniz için bizimle iletişime geçin.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:shadow-lg hover:shadow-purple-500/30 transition"
            >
              İletişime geç
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/features"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white font-medium transition"
            >
              Özellikleri incele
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Security;
